<template>
  <div class="guarantee">
    <el-form :model="filterForm" label-suffix="：" inline size="small">
      <el-form-item label="业务类型">
        <el-select
          v-model.trim="filterForm.bizType"
          placeholder="请选择"
          clearable
        >
          <el-option
            v-for="item in bizTypeOption"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目名称">
        <el-input
          v-model.trim="filterForm.projectName"
          placeholder="请输入"
          maxlength="100"
          clearable
        />
      </el-form-item>
      <el-form-item label="收款账户">
        <el-input
          v-model.trim="filterForm.payeeAccNo"
          placeholder="请输入"
          maxlength="100"
          clearable
        />
      </el-form-item>
      <el-form-item label="创建时间">
        <jczh-date-picker
          v-model="timeRange"
          type="datetimeRange"
          :default-time="['00:00:00', '23:59:59']"
        />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filterForm.status" clearable>
          <el-option
            v-for="item in guaranteeFeeStatusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="已缴金额">
        <div class="flex col-center">
          <el-input-number
            v-model.trim="filterForm.startEntryAmount"
            placeholder="请输入"
            :min="0"
            :max="99999999999.99"
            :controls="false"
            :precision="2"
          />
          <span class="m-r-10 m-l-10">-</span>
          <el-input-number
            v-model.trim="filterForm.endEntryAmount"
            placeholder="请输入"
            :min="0"
            :max="99999999999.99"
            :controls="false"
            :precision="2"
          />
        </div>
      </el-form-item>
      <el-form-item>
        <div class="flex">
          <el-button type="primary" size="small" @click="handleSearch">
            查询
          </el-button>
          <el-button size="small" @click="handleReset">
            重置
          </el-button>
        </div>
      </el-form-item>
    </el-form>

    <jczh-table-content
      :data="tableData"
      :option="tableOption"
      pagination
      :total="total"
      :page.sync="page"
      :limit.sync="size"
      table-id="id"
    >
      <template v-slot:last>
        <el-table-column label="操作" align="center" width="80px" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="showReturnButton(row)"
              type="text"
              @click="returnGuaranteeFee(row)"
            >申请退回</el-button>
            <div v-else>-</div>
          </template>
        </el-table-column>
      </template>
    </jczh-table-content>
  </div>
</template>

<script>
import {
  abnormalGuaranteeFeeStatusDict,
  guaranteeFeeBizTypeDict
} from '@/dictionary/financial'
import { listFilterMixin } from 'jczh-ui'
import { bankCardFilter, strToThousands } from '@/utils/utils'
import {
  applyReturnGuaranteeApi,
  getAbnormalGuaranteePageApi
} from '@/api/financial/guarantee'

export default {
  name: 'GuaranteeReturn',

  mixins: [listFilterMixin],

  data() {
    return {
      filterForm: {
        // 业务类型/采购类型
        bizType: '',
        // 项目名称
        projectName: '',
        // 创建开始时间
        startTime: '',
        // 创建结束时间
        endTime: '',
        // 收款账户
        payeeAccNo: '',
        // 保证金缴纳状态
        status: '',
        // 已缴纳金额-起始金额
        startEntryAmount: undefined,
        // 已缴纳金额-结束金额
        endEntryAmount: undefined
      },
      tableData: [],
      total: 0,
      tableOption: [
        {
          label: '创建时间',
          prop: 'createTime'
        },
        {
          label: '业务类型',
          prop: 'bizType',
          filter: value =>
            guaranteeFeeBizTypeDict.translate(value)?.label || '-'
        },
        {
          label: '项目名称',
          prop: 'projectName'
        },
        {
          label: '收款账户',
          prop: 'payeeAccNo',
          minWidth: 120,
          filter: value => (value ? bankCardFilter(value) : '-')
        },
        {
          label: '收款户名',
          prop: 'payeeAccName'
        },
        {
          label: '开户银行',
          prop: 'bankType'
        },
        {
          label: '已缴金额（元）',
          prop: 'entryAmount',
          minWidth: 120,
          columnAttrs: {
            align: 'right',
            headerAlign: 'center'
          },
          filter: value => (value ? strToThousands(value) : '-')
        },
        {
          label: '状态',
          prop: 'status',
          minWidth: 120,
          filter: value =>
            abnormalGuaranteeFeeStatusDict.translate(value)?.label || '-'
        }
      ]
    }
  },

  computed: {
    // 业务类型/采购类型选项
    bizTypeOption() {
      return [
        { label: '全部', value: '' },
        guaranteeFeeBizTypeDict.ZB,
        guaranteeFeeBizTypeDict.XB,
        guaranteeFeeBizTypeDict.JZ,
        {
          label: '谈判采购',
          value: `${guaranteeFeeBizTypeDict.TP.value},${guaranteeFeeBizTypeDict.HTP.value}`
        }
      ]
    },

    timeRange: {
      get() {
        return [this.filterForm.startTime, this.filterForm.endTime]
      },
      set(val) {
        this.filterForm.startTime = val[0]
        this.filterForm.endTime = val[1]
      }
    },

    // 保证金缴纳状态选项
    guaranteeFeeStatusOptions() {
      return [
        { label: '全部', value: '' },
        ...abnormalGuaranteeFeeStatusDict.array
      ]
    }
  },

  created() {
    this.getList()
  },

  methods: {
    /**
     * 判断是否显示申请退回按钮
     */
    showReturnButton(row) {
      return (
        Number(row.status) === abnormalGuaranteeFeeStatusDict.PENDING.value ||
        Number(row.status) === abnormalGuaranteeFeeStatusDict.TIMEOUT.value
      )
    },

    /**
     * 申请退回
     */
    returnGuaranteeFee(row) {
      this.$confirm('确认后资金将退回汇款人账户，请注意查收', '申请退回确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          applyReturnGuaranteeApi(row.depositReceiptEntryId)
            .then(res => {
              this.$message.success('操作成功')
              this.getList()
            })
            .catch(err => {
              console.error('申请退回失败', err)
              if (err?.data?.message) this.$message.error(err?.data?.message)
            })
        })
        .catch(() => {})
    },

    /**
     * 列表查询
     */
    getList() {
      this.epitaxy()

      const requestParams = {
        pageNum: this.page,
        pageSize: this.size,
        ...this.filterForm
      }
      if (this.filterForm.bizType) {
        requestParams.bizType = this.filterForm.bizType.split(',')
      }
      getAbnormalGuaranteePageApi(requestParams)
        .then(res => {
          this.tableData = res.data?.records || []
          this.total = res.data?.total || 0
        })
        .catch(err => {
          console.error('异常保证金列表查询失败', err)
          if (err?.data?.message) this.$message.error(err?.data?.message)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.guarantee {
  width: 100%;
  background: $white-color;
  border-radius: 4px;
  padding: 20px;
}
</style>
